import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import { loadingRedirect } from 'utils/redirect';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { useSelector } from 'react-redux';
import useActions from 'hooks/useActions';
import { i18n } from 'utils/i18n';
import { loadingOverlay } from 'utils/dom';
import { sanitize } from 'utils/validation';
import { MODAL, ASSETS, THEMES, SESSION_STORAGE } from 'constants';
import { isFetchingEndpointSelector } from 'selectors/fetchingSelectors';
import Title from 'components/Title';
import ModifyReservationModal from 'components/modals/ModifyReservationModal';
import ChangeReturnLocationModal from 'components/modals/ChangeReturnLocationModal';
import TaxesModal from 'components/modals/TaxesModal';
import PostResLayout from 'components/PostRes/PostResLayout';
import PoliciesComponent from 'components/PoliciesComponent';
import RentalBranchLocation from 'components/RentalBranchLocation/RentalBranchLocation';
import RentalDetailsAfterHoursModal from 'components/modals/RentalDetailsAfterHoursModal';
import { openModal } from 'actions/modal/openModal';
import RenterInformation from '../RenterInformation';

const FORMAT = 'llll';

const ConfirmedRental = (props) => {
  const { policies, rentalReady, firstName, ticketNumber, locationType, isReturnUpdated, details } = props || {};
  const showAHViewDetailsModal = useActions(() => openModal(MODAL.RENTAL_DETAILS_AFTER_HOURS_MODAL));
  const showViewDetailsModal = useActions(() => openModal(MODAL.RENTAL_DETAILS_TAXES_FEES_MODAL));
  const showChangeReturnLocationDetailsModal = useActions(() =>
    openModal(MODAL.CHANGE_RETURN_LOCATION_MODAL, { skipAnalytics: true })
  );
  const isFetchingSession = useSelector((state) => isFetchingEndpointSelector(state, { endpoint: 'session/current' }));
  const isFetchingLogout = useSelector((state) => isFetchingEndpointSelector(state, { endpoint: 'profile/logout' }));
  const termsAndConditionsRef = useRef(null);

  useEffect(() => {
    // when entering confirmed / updated rental flow show the overlay if we're fetching the session
    isFetchingSession ? loadingOverlay() : loadingOverlay(false);
  }, [isFetchingSession]);

  useEffect(() => {
    if (!isFetchingLogout && !isFetchingSession && !ticketNumber) {
      loadingRedirect(WINDOW_OBJECT_KEYS.FIND_A_RESERVATION);
    }
  }, [isFetchingLogout]);

  const returnTime = sessionStorage.getItem(SESSION_STORAGE.MODIFY_RENTAL_ORIGINAL_DATE);

  const updatedTime = utils.getDateTimeObjFromTs(props.returnTime).format(FORMAT);

  return (
    <>
      <PostResLayout noMinHeight>
        {!isFetchingSession && (
          <div className='rental-updated'>
            {isReturnUpdated && (
              <div className='rental-updated__confirmed-title'>
                <span
                  dangerouslySetInnerHTML={sanitize(i18n('rental_agreement_updated_copy', [returnTime, updatedTime]))}
                />
              </div>
            )}
            {!details && (
              <Title
                type='h1'
                className='rental-updated__title'
                text={i18n(
                  (props.returnConfirmed && !isReturnUpdated) || !isReturnUpdated
                    ? 'return_confirmed'
                    : 'return_updated'
                )}
              />
            )}
            <p className='rental-updated__title-subheading-title'>
              {i18n('trip_card_agreement_number', [ticketNumber])}
            </p>
            <p className='rental-updated__title-subheading-title-copy'>
              {i18n('rental_agreement_copy', [firstName, updatedTime], { jsx: true })}
            </p>
          </div>
        )}
      </PostResLayout>

      <PostResLayout theme={THEMES.LIGHT_BLUE} noMinHeight noMarignTop noPaddingTopBottom>
        {rentalReady && (
          <RenterInformation
            {...props}
            showViewDetailsModal={showViewDetailsModal}
            showAHViewDetailsModal={showAHViewDetailsModal}
            showChangeReturnLocationDetailsModal={showChangeReturnLocationDetailsModal}
            isRental
          />
        )}
      </PostResLayout>

      <PostResLayout noMinHeight noMarignTop noPaddingTopBottom>
        {rentalReady && <RentalBranchLocation isRental rentalLocationType={locationType} />}
      </PostResLayout>

      {!!policies?.length && (
        <div ref={termsAndConditionsRef}>
          <PostResLayout theme={THEMES.LIGHT_BLUE} noMinHeight noMarignTop>
            <PoliciesComponent
              title={i18n('stc_confirmation_your_rentals_policies')}
              policies={policies}
              backgroundArtURL={ASSETS.RENTAL_POLICIES_PLACEHOLDER}
              backgroundArtAlt={i18n('policy_background_image_alt')}
              hideTermsConditionsCta
              isRental
            />
          </PostResLayout>
        </div>
      )}

      <RentalDetailsAfterHoursModal />
      <TaxesModal />
      <ModifyReservationModal />
      <ChangeReturnLocationModal />
    </>
  );
};

ConfirmedRental.propTypes = {
  details: PropTypes.bool,
};

ConfirmedRental.defaultProps = {
  details: false, // hide the banner,if details is true.
};

export default ConfirmedRental;
